import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Terminal, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const JavaScriptEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapeJavaScript = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Escape backslashes first to avoid conflicts
    const backslashMatches = result.match(/\\/g);
    if (backslashMatches) {
      entitiesCount += backslashMatches.length;
      result = result.replace(/\\/g, '\\\\');
    }

    // Escape double quotes
    const doubleQuoteMatches = result.match(/"/g);
    if (doubleQuoteMatches) {
      entitiesCount += doubleQuoteMatches.length;
      result = result.replace(/"/g, '\\"');
    }

    // Escape single quotes
    const singleQuoteMatches = result.match(/'/g);
    if (singleQuoteMatches) {
      entitiesCount += singleQuoteMatches.length;
      result = result.replace(/'/g, "\\'");
    }

    // Escape newlines
    const newlineMatches = result.match(/\n/g);
    if (newlineMatches) {
      entitiesCount += newlineMatches.length;
      result = result.replace(/\n/g, '\\n');
    }

    // Escape carriage returns
    const carriageReturnMatches = result.match(/\r/g);
    if (carriageReturnMatches) {
      entitiesCount += carriageReturnMatches.length;
      result = result.replace(/\r/g, '\\r');
    }

    // Escape tabs
    const tabMatches = result.match(/\t/g);
    if (tabMatches) {
      entitiesCount += tabMatches.length;
      result = result.replace(/\t/g, '\\t');
    }

    // Escape backspace
    const backspaceMatches = result.match(/\b/g);
    if (backspaceMatches) {
      entitiesCount += backspaceMatches.length;
      result = result.replace(/\b/g, '\\b');
    }

    // Escape form feed
    const formFeedMatches = result.match(/\f/g);
    if (formFeedMatches) {
      entitiesCount += formFeedMatches.length;
      result = result.replace(/\f/g, '\\f');
    }

    // Escape vertical tab
    const verticalTabMatches = result.match(/\v/g);
    if (verticalTabMatches) {
      entitiesCount += verticalTabMatches.length;
      result = result.replace(/\v/g, '\\v');
    }

    // Escape null character
    const nullCharMatches = result.match(/\0/g);
    if (nullCharMatches) {
      entitiesCount += nullCharMatches.length;
      result = result.replace(/\0/g, '\\0');
    }

    // Escape backticks for template literals
    const backtickMatches = result.match(/`/g);
    if (backtickMatches) {
      entitiesCount += backtickMatches.length;
      result = result.replace(/`/g, '\\`');
    }

    // Escape dollar signs for template literals
    const dollarMatches = result.match(/\$/g);
    if (dollarMatches) {
      entitiesCount += dollarMatches.length;
      result = result.replace(/\$/g, '\\$');
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeJavaScript = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Unescape Unicode sequences (\uXXXX)
    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 65535) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape hexadecimal sequences (\xXX)
    result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape octal sequences (\XXX)
    result = result.replace(/\\([0-7]{1,3})/g, (match, oct) => {
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 255 && oct !== '0') {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape newlines
    const newlineEscapeMatches = result.match(/\\n/g);
    if (newlineEscapeMatches) {
      entitiesCount += newlineEscapeMatches.length;
      result = result.replace(/\\n/g, '\n');
    }

    // Unescape carriage returns
    const carriageReturnEscapeMatches = result.match(/\\r/g);
    if (carriageReturnEscapeMatches) {
      entitiesCount += carriageReturnEscapeMatches.length;
      result = result.replace(/\\r/g, '\r');
    }

    // Unescape tabs
    const tabEscapeMatches = result.match(/\\t/g);
    if (tabEscapeMatches) {
      entitiesCount += tabEscapeMatches.length;
      result = result.replace(/\\t/g, '\t');
    }

    // Unescape backspace
    const backspaceEscapeMatches = result.match(/\\b/g);
    if (backspaceEscapeMatches) {
      entitiesCount += backspaceEscapeMatches.length;
      result = result.replace(/\\b/g, '\b');
    }

    // Unescape form feed
    const formFeedEscapeMatches = result.match(/\\f/g);
    if (formFeedEscapeMatches) {
      entitiesCount += formFeedEscapeMatches.length;
      result = result.replace(/\\f/g, '\f');
    }

    // Unescape vertical tab
    const verticalTabEscapeMatches = result.match(/\\v/g);
    if (verticalTabEscapeMatches) {
      entitiesCount += verticalTabEscapeMatches.length;
      result = result.replace(/\\v/g, '\v');
    }

    // Unescape null character
    const nullCharEscapeMatches = result.match(/\\0/g);
    if (nullCharEscapeMatches) {
      entitiesCount += nullCharEscapeMatches.length;
      result = result.replace(/\\0/g, '\0');
    }

    // Unescape double quotes
    const doubleQuoteEscapeMatches = result.match(/\\"/g);
    if (doubleQuoteEscapeMatches) {
      entitiesCount += doubleQuoteEscapeMatches.length;
      result = result.replace(/\\"/g, '"');
    }

    // Unescape single quotes
    const singleQuoteEscapeMatches = result.match(/\\'/g);
    if (singleQuoteEscapeMatches) {
      entitiesCount += singleQuoteEscapeMatches.length;
      result = result.replace(/\\'/g, "'");
    }

    // Unescape backticks
    const backtickEscapeMatches = result.match(/\\`/g);
    if (backtickEscapeMatches) {
      entitiesCount += backtickEscapeMatches.length;
      result = result.replace(/\\`/g, '`');
    }

    // Unescape dollar signs
    const dollarEscapeMatches = result.match(/\\\$/g);
    if (dollarEscapeMatches) {
      entitiesCount += dollarEscapeMatches.length;
      result = result.replace(/\\\$/g, '$');
    }

    // Unescape backslashes (must be last)
    const backslashEscapeMatches = result.match(/\\\\/g);
    if (backslashEscapeMatches) {
      entitiesCount += backslashEscapeMatches.length;
      result = result.replace(/\\\\/g, '\\');
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeJavaScript : unescapeJavaScript;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeJavaScript, unescapeJavaScript]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-javascript.js`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleJavaScript = (): void => {
    setInputText(`// Sample JavaScript code with special characters
function greetUser(name) {
    const message = "Hello, " + name + "!\\nWelcome to our app.";
    const template = \`User: \${name}
Status: Active
Quote: "Programming is fun!"
Path: C:\\\\Users\\\\Documents\\\\file.txt\`;
    
    console.log(message);
    console.log(template);
    
    // Special characters test
    const specialChars = {
        tab: "\\t",
        newline: "\\n",
        backslash: "\\\\",
        quote: "\\"",
        apostrophe: "'",
        formFeed: "\\f",
        verticalTab: "\\v",
        nullChar: "\\0"
    };
    
    return \`Result: \${JSON.stringify(specialChars)}\`;
}`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`// Sample escaped JavaScript code
function greetUser(name) {
    const message = \\"Hello, \\" + name + \\"!\\\\nWelcome to our app.\\";
    const template = \\\`User: \\\${name}
Status: Active
Quote: \\\\"Programming is fun!\\\\"
Path: C:\\\\\\\\Users\\\\\\\\Documents\\\\\\\\file.txt\\\`;
    
    console.log(message);
    console.log(template);
    
    // Special characters test
    const specialChars = {
        tab: \\"\\\\t\\",
        newline: \\"\\\\n\\",
        backslash: \\"\\\\\\\\\\",
        quote: \\"\\\\\\"\\",
        apostrophe: \\"'\\",
        formFeed: \\"\\\\f\\",
        verticalTab: \\"\\\\v\\",
        nullChar: \\"\\\\0\\"
    };
    
    return \\\`Result: \\\${JSON.stringify(specialChars)}\\\`;
}`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-yellow-900 to-orange-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg">
              <Terminal className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">JavaScript Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced JavaScript string processor with TypeScript analytics</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-yellow-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape JavaScript</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-orange-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape JavaScript</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Escape Sequences Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported JavaScript Escape Sequences</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Basic:</strong> \\\\ \\" \\' \\n \\r \\t
                </div>
                <div className="text-gray-300">
                  <strong>Control:</strong> \\b \\f \\v \\0
                </div>
                <div className="text-gray-300">
                  <strong>Template:</strong> \\$ \\`
                </div>
                <div className="text-gray-300">
                  <strong>Unicode:</strong> \\uXXXX \\xXX \\XXX
                </div>
                <div className="text-gray-300">
                  <strong>Range:</strong> U+0000 - U+FFFF
                </div>
                <div className="text-gray-300">
                  <strong>Safe:</strong> No double escaping
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Large input/output areas */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".js,.ts,.txt,.json"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter JavaScript code to escape...' : 'Enter escaped JavaScript to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed JavaScript output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleJavaScript}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample JavaScript
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={() => setInputText(JSON.stringify(inputText, null, 2))}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              JSON Stringify
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default JavaScriptEscapeUnescapeTool;